/**
 * Who has a compliance gap, and in what. A well-run organisation has most
 * staff fully compliant, so gaps are concentrated in a small, believable set
 * (~14% of active staff): the newest starters still onboarding, a couple of
 * casuals not yet enrolled in eTrainu, the NDIS Worker Screening exceptions,
 * and a handful of long-serving staff with a lapsed CPR, an unfinished module
 * or a licence never uploaded. Everyone else is current on every item.
 *
 * training.ts (eTrainu records) and compliance.ts (EH certifications) both
 * read this plan, so the Training and Workplace Compliance pages agree.
 */
import { rng } from '../../rng';
import { Staff } from '../../world';
import { activeToday, daily, daysBetween, isCare, isFrontline, isRemote, shiftDay, today, typeOf } from './common';

export type GapKind = 'missing' | 'in_progress' | 'expired';

interface GapItem {
    name: string;
    source: 'EH' | 'ET';
    applies: (s: Staff) => boolean;
    /** Can lapse (has a validity period). */
    expires: boolean;
    /** Relative likelihood a gap lands on this item. */
    weight: number;
}

const all = () => true;

/** Every compliance item except NDIS Worker Screening (chosen explicitly) and AHPRA (always current). */
const GAP_ITEMS: GapItem[] = [
    { name: 'Police Clearance', source: 'EH', applies: all, expires: true, weight: 1.5 },
    { name: 'Working with Children Check', source: 'EH', applies: all, expires: true, weight: 2 },
    { name: 'Drivers Licence', source: 'EH', applies: all, expires: true, weight: 2.5 },
    { name: 'Code of Conduct & Induction', source: 'EH', applies: all, expires: false, weight: 0.5 },
    { name: 'NDIS Worker Orientation Module', source: 'ET', applies: all, expires: false, weight: 0.5 },
    { name: 'Cultural Safety', source: 'ET', applies: all, expires: true, weight: 1 },
    { name: 'Infection Control', source: 'ET', applies: all, expires: true, weight: 1.5 },
    { name: 'Manual Handling', source: 'ET', applies: all, expires: true, weight: 1.5 },
    // Newly mandated this year — rollout still catching up, the one visible problem spot.
    { name: 'DFV Awareness', source: 'ET', applies: all, expires: false, weight: 10 },
    { name: 'First Aid', source: 'ET', applies: isFrontline, expires: true, weight: 2 },
    { name: 'CPR', source: 'ET', applies: isFrontline, expires: true, weight: 5 },
    { name: 'Trauma-Informed Practice', source: 'ET', applies: isFrontline, expires: false, weight: 4 },
    { name: 'Medication Administration', source: 'ET', applies: isCare, expires: true, weight: 2 },
    { name: 'Remote Driving 4WD', source: 'ET', applies: s => isFrontline(s) && isRemote(s), expires: true, weight: 3.5 },
];

/** eTrainu courses that count towards a compliance category. */
export const COMPLIANCE_COURSES = new Set(GAP_ITEMS.filter(i => i.source === 'ET').map(i => i.name));

/** Share of active staff with at least one gap. */
const GAP_SHARE = 0.145;
/** Tenure under which a starter is still working through onboarding modules. */
const ONBOARDING_DAYS = 42;

/**
 * Active staff with no eTrainu account yet — the newest starters plus a
 * couple of casuals HR hasn't enrolled ("Training Gaps" chips).
 */
export function untrackedStaff(): Staff[] {
    return daily('people:untracked', () => {
        const active = [...activeToday()].sort((a, b) => b.startDate.localeCompare(a.startDate) || a.name.localeCompare(b.name));
        const newest = active.slice(0, 3);
        const casuals = rng('people:untracked:casuals').sample(
            active.slice(3).filter(s => typeOf(s) === 'casual'),
            2,
        );
        return [...newest, ...casuals];
    });
}

/**
 * NDIS Worker Screening is the regulator's headline item, so its gaps are
 * chosen explicitly: the two newest frontline starters are still awaiting
 * their clearance and one long-serving worker's clearance has lapsed.
 */
export function ndisGaps(): { missing: Set<number>; expired: Map<number, string> } {
    return daily('people:ndis:gaps', () => {
        const frontline = activeToday().filter(isFrontline);
        const newest = [...frontline].sort((a, b) => b.startDate.localeCompare(a.startDate)).slice(0, 2);
        const others = frontline.filter(s => !newest.includes(s) && daysBetween(s.startDate, today()) > 900).sort((a, b) => a.id - b.id);
        const lapsed = rng('people:ndis:lapsed').pick(others.length ? others : frontline);
        return { missing: new Set(newest.map(s => s.id)), expired: new Map([[lapsed.id, shiftDay(today(), -11)]]) };
    });
}

function pickWeighted(r: ReturnType<typeof rng>, items: GapItem[], n: number): GapItem[] {
    const pool = [...items];
    const out: GapItem[] = [];
    while (out.length < n && pool.length) {
        const total = pool.reduce((a, i) => a + i.weight, 0);
        let x = r.next() * total;
        let idx = 0;
        for (; idx < pool.length - 1; idx++) {
            x -= pool[idx].weight;
            if (x < 0) {
                break;
            }
        }
        out.push(pool.splice(idx, 1)[0]);
    }
    return out;
}

function kindFor(r: ReturnType<typeof rng>, item: GapItem): GapKind {
    if (item.expires && r.next() < 0.65) {
        return 'expired';
    }
    return item.source === 'ET' && r.next() < 0.3 ? 'in_progress' : 'missing';
}

/** staffId → (item name → gap). Staff absent from the map are fully compliant. */
export function gapPlan(): Map<number, Map<string, GapKind>> {
    return daily('people:gaps:plan', () => {
        const t = today();
        const active = [...activeToday()].sort((a, b) => a.id - b.id);
        const plan = new Map<number, Map<string, GapKind>>();
        const add = (s: Staff, item: string, kind: GapKind) => {
            const m = plan.get(s.id) ?? new Map<string, GapKind>();
            m.set(item, kind);
            plan.set(s.id, m);
        };
        const untracked = new Set(untrackedStaff().map(s => s.id));
        const ndis = ndisGaps();

        // Untracked staff have no eTrainu records at all (every ET item reads missing); NDIS gaps are counted in compliance.ts.
        for (const s of active) {
            if (untracked.has(s.id) || ndis.missing.has(s.id) || ndis.expired.has(s.id)) {
                plan.set(s.id, plan.get(s.id) ?? new Map());
            }
        }

        // Recent starters part-way through their onboarding modules.
        for (const s of active.filter(x => !untracked.has(x.id) && daysBetween(x.startDate, t) < ONBOARDING_DAYS)) {
            const r = rng(`people:gaps:onboarding:${s.id}`);
            const items = GAP_ITEMS.filter(i => i.source === 'ET' && i.applies(s));
            for (const item of pickWeighted(r, items, r.int(2, 4))) {
                add(s, item.name, r.bool(0.45) ? 'in_progress' : 'missing');
            }
        }

        // Top up with a stable handful of established staff (lowest per-staff score first, so membership doesn't churn daily).
        const target = Math.round(active.length * GAP_SHARE);
        const rest = active
            .filter(s => !plan.has(s.id) && daysBetween(s.startDate, t) >= ONBOARDING_DAYS)
            .map(s => ({ s, score: rng(`people:gaps:score:${s.id}`).next() }))
            .sort((a, b) => a.score - b.score);
        for (const { s } of rest) {
            if (plan.size >= target) {
                break;
            }
            const r = rng(`people:gaps:items:${s.id}`);
            const n = r.weighted([[1, 4], [2, 4], [3, 2]] as [number, number][]);
            for (const item of pickWeighted(r, GAP_ITEMS.filter(i => i.applies(s)), n)) {
                add(s, item.name, kindFor(r, item));
            }
        }
        return plan;
    });
}

/** The gap a staff member has on an item, or null when they're current on it. */
export function gapFor(staffId: number, item: string): GapKind | null {
    return gapPlan().get(staffId)?.get(item) ?? null;
}
